// Touch pull-to-refresh for tab screens. Scrolling happens in the nearest
// `[data-scroll-root]` ancestor (TabLayout's content region — see
// TabLayout.tsx), so the gesture arms only when THAT element is at the top;
// falls back to the document scrolling element when there is no such
// ancestor (e.g. rendered standalone, as in this component's own tests).
// Native listeners — React's touch events are passive, and owning the
// gesture needs preventDefault once a pull is recognized (otherwise the
// WebView's own overscroll fights the indicator).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';

/** Pull distance (post-damping, px) that commits a refresh on release. */
const THRESHOLD_PX = 64;
const MAX_PULL_PX = 96;
/** Finger travel is damped so the content trails the gesture. */
const DAMPING = 2.2;

export function PullToRefresh({
  onRefresh,
  children,
}: {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}): ReactNode {
  const t = useT();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Refs mirror the state the listeners need — the listeners are bound once
  // per onRefresh and must not close over stale render values.
  const startYRef = useRef<number | null>(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const el = rootRef.current;
    if (el === null) return;
    const scrollParent = (): Element =>
      el.closest('[data-scroll-root]') ?? document.scrollingElement ?? document.documentElement;
    const atTop = (): boolean => (scrollParent().scrollTop || 0) <= 0;
    const apply = (px: number): void => {
      pullRef.current = px;
      setPull(px);
    };

    const onStart = (e: TouchEvent): void => {
      if (refreshingRef.current || !atTop()) return;
      startYRef.current = e.touches[0]?.clientY ?? null;
    };
    const onMove = (e: TouchEvent): void => {
      const startY = startYRef.current;
      if (startY === null || refreshingRef.current) return;
      const dy = (e.touches[0]?.clientY ?? startY) - startY;
      if (dy <= 0 || !atTop()) {
        apply(0);
        return;
      }
      if (e.cancelable) e.preventDefault();
      apply(Math.min(dy / DAMPING, MAX_PULL_PX));
    };
    const onEnd = (): void => {
      if (startYRef.current === null) return;
      startYRef.current = null;
      if (pullRef.current >= THRESHOLD_PX && !refreshingRef.current) {
        refreshingRef.current = true;
        setRefreshing(true);
        apply(THRESHOLD_PX);
        void onRefresh().finally(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          apply(0);
        });
      } else {
        apply(0);
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh]);

  const active = pull > 0 || refreshing;
  const dragging = startYRef.current !== null;
  return (
    <div ref={rootRef} className="relative" data-testid="pull-to-refresh">
      <div
        role="status"
        className="pointer-events-none absolute inset-x-0 top-0 flex items-end justify-center overflow-hidden"
        style={{ height: pull, opacity: active ? 1 : 0 }}
      >
        <span
          aria-hidden="true"
          className={`mb-2 h-6 w-6 rounded-full border-2 border-accent border-t-transparent ${
            refreshing ? 'animate-spin' : ''
          }`}
          style={refreshing ? undefined : { transform: `rotate(${pull * 3}deg)` }}
        />
        <span className="sr-only">{refreshing ? t('sync.refreshing') : ''}</span>
      </div>
      <div
        style={{
          transform: active ? `translateY(${pull}px)` : undefined,
          transition: dragging && !refreshing ? undefined : 'transform 200ms',
        }}
      >
        {children}
      </div>
    </div>
  );
}
